import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { db } from '../../firebase';
import { collection, getDocs, addDoc, serverTimestamp } from 'firebase/firestore';
import { useAuth } from '../../context/AuthContext';
import Layout from '../../components/ui/Layout';
import { logAudit, AUDIT_ACTIONS } from '../../utils/audit';
import { IconTruck, IconLock, IconCheck, IconX, IconPlus, IconSearch, IconUser } from '@tabler/icons-react';

export default function AssetList() {
  const { orgId, userProfile } = useAuth();
  const navigate = useNavigate();
  const [assets, setAssets] = useState([]);
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [assetReg, setAssetReg] = useState('');
  const [assetVin, setAssetVin] = useState('');
  const [assetSerial, setAssetSerial] = useState('');
  const [assetMake, setAssetMake] = useState('');
  const [assetModel, setAssetModel] = useState('');
  const [assetType, setAssetType] = useState('vehicle');
  const [assetPool, setAssetPool] = useState(false);
  const [assignedTo, setAssignedTo] = useState(null);
  const [dvlaLoading, setDvlaLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [modalStep, setModalStep] = useState(1);

  const branchId = userProfile?.branchId;
  const resolvedOrgId = orgId || userProfile?.orgId;

  const loadAssets = async () => {
    if (!resolvedOrgId || !branchId) return;
    try {
      const snap = await getDocs(
        collection(db, 'organisations', resolvedOrgId, 'branches', branchId, 'assets')
      );
      setAssets(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (err) {
      console.error(err);
    }
    setLoading(false);
  };

  const loadStaff = async () => {
    if (!resolvedOrgId || !branchId) return;
    try {
      const snap = await getDocs(collection(db, 'organisations', resolvedOrgId, 'users'));
      const branchStaff = snap.docs
        .map(d => ({ id: d.id, ...d.data() }))
        .filter(u => (u.role === 'operator' || u.role === 'driver') && u.branchId === branchId);
      setStaff(branchStaff);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    if (resolvedOrgId && branchId) {
      loadAssets();
      loadStaff();
    } else {
      setLoading(false);
    }
  }, [resolvedOrgId, branchId]);

  const handleDvlaLookup = async () => {
    if (!assetReg) return;
    setDvlaLoading(true);
    await new Promise(r => setTimeout(r, 800));
    setAssetMake('Ford');
    setAssetModel('Transit');
    setAssetType('vehicle');
    setDvlaLoading(false);
  };

  const resetModal = () => {
    setAssetReg('');
    setAssetVin('');
    setAssetSerial('');
    setAssetMake('');
    setAssetModel('');
    setAssetType('vehicle');
    setAssetPool(false);
    setAssignedTo(null);
    setModalStep(1);
    setShowModal(false);
  };

  const handleNext = () => {
    if (assetPool) {
      handleAddAsset(null);
    } else {
      setModalStep(2);
    }
  };

  const handleAddAsset = async (staffMember) => {
    setSaving(true);
    try {
      const assetName = `${assetMake} ${assetModel}${assetReg ? ` · ${assetReg}` : ''}`;

      const docRef = await addDoc(
        collection(db, 'organisations', resolvedOrgId, 'branches', branchId, 'assets'),
        {
          reg: assetReg || null,
          vin: assetVin || null,
          serialNumber: assetSerial || null,
          make: assetMake,
          model: assetModel,
          type: assetType,
          isPool: assetPool,
          isBlocked: false,
          blockedReason: null,
          assignedTo: staffMember?.name || null,
          assignedToId: staffMember?.id || null,
          departmentId: staffMember?.departmentId || userProfile?.departmentId || null,
          createdAt: serverTimestamp(),
        }
      );

      await logAudit(resolvedOrgId, {
        action: AUDIT_ACTIONS.ASSET_CREATED,
        performedBy: userProfile?.id,
        performedByName: userProfile?.name,
        targetId: docRef.id,
        targetType: 'asset',
        targetName: assetName,
        details: `${assetType} added${staffMember ? ` — assigned to ${staffMember.name}` : assetPool ? ' — pool asset' : ''}`,
        branchId,
      });

      resetModal();
      await loadAssets();
    } catch (err) {
      console.error(err);
    }
    setSaving(false);
  };

  const filtered = assets.filter(a => {
    const q = search.toLowerCase();
    return !q ||
      (a.reg?.toLowerCase().includes(q)) ||
      (a.make?.toLowerCase().includes(q)) ||
      (a.model?.toLowerCase().includes(q)) ||
      (a.vin?.toLowerCase().includes(q)) ||
      (a.serialNumber?.toLowerCase().includes(q));
  });

  const getInitials = name => name?.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2) || '?';

  const avatarColours = [
    { bg: '#E6F1FB', color: '#185FA5' },
    { bg: '#EAF3DE', color: '#3B6D11' },
    { bg: '#EEEDFE', color: '#534AB7' },
    { bg: '#FCEBEB', color: '#A32D2D' },
    { bg: '#FDF0E8', color: '#993C1D' },
    { bg: '#E1F5EE', color: '#085041' },
  ];

  return (
    <Layout>
      <div style={{ padding: 'var(--main-padding)' }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 24 }}>
          <div>
            <h1 style={{ fontSize: 22, fontWeight: 400, marginBottom: 4 }}>Assets</h1>
            <p style={{ fontSize: 13, color: 'var(--color-text-tertiary)' }}>
              {branchId ? branchId.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ') : 'All branches'} · {filtered.length} assets · {filtered.filter(a => a.isBlocked).length} blocked
            </p>
          </div>
          <button
            onClick={() => setShowModal(true)}
            style={{ background: 'var(--color-brand)', color: '#fff', border: 'none', borderRadius: 'var(--radius-md)', padding: '8px 16px', fontSize: 13, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}
          >
            <IconPlus size={15} stroke={1.5} /> Add asset
          </button>
        </div>

        <div style={{ position: 'relative', marginBottom: 16, maxWidth: 360 }}>
          <IconSearch size={15} stroke={1.5} color="var(--color-text-tertiary)" style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)' }} />
          <input type="text" placeholder="Search by reg, make, model, VIN or serial" value={search} onChange={e => setSearch(e.target.value)} style={{ paddingLeft: 36, width: '100%', boxSizing: 'border-box', background: '#fff' }} />
        </div>

        <div style={{ background: 'var(--color-bg-primary)', border: '0.5px solid var(--color-border-light)', borderRadius: 'var(--radius-lg)', overflow: 'hidden' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1.8fr 1.2fr 1fr 0.8fr 0.8fr 80px', gap: 0, padding: '8px 16px', borderBottom: '0.5px solid var(--color-border-light)', background: 'var(--color-bg-secondary)' }}>
            {['Asset', 'Assigned to', 'Last check', 'MOT', 'Status', ''].map(h => (
              <div key={h} style={{ fontSize: 10, fontWeight: 400, color: 'var(--color-text-tertiary)', letterSpacing: '0.04em' }}>{h}</div>
            ))}
          </div>

          {loading ? (
            <div style={{ padding: 20, textAlign: 'center', fontSize: 13, color: 'var(--color-text-tertiary)' }}>Loading...</div>
          ) : filtered.length === 0 ? (
            <div style={{ padding: 40, textAlign: 'center' }}>
              <IconTruck size={32} color="var(--color-text-tertiary)" stroke={1.5} style={{ marginBottom: 12 }} />
              <div style={{ fontSize: 14, fontWeight: 400, color: 'var(--color-text-primary)', marginBottom: 6 }}>No assets yet</div>
              <div style={{ fontSize: 13, color: 'var(--color-text-tertiary)' }}>Click Add asset to get started</div>
            </div>
          ) : filtered.map((asset, i) => (
            <div
              key={asset.id}
              onClick={() => navigate(`/assets/${asset.id}`)}
              style={{ display: 'grid', gridTemplateColumns: '1.8fr 1.2fr 1fr 0.8fr 0.8fr 80px', gap: 0, padding: '13px 16px', borderBottom: i < filtered.length - 1 ? '0.5px solid var(--color-border-light)' : 'none', background: asset.isBlocked ? '#FCEBEB' : 'transparent', cursor: 'pointer', alignItems: 'center' }}
              onMouseEnter={e => !asset.isBlocked && (e.currentTarget.style.background = 'var(--color-bg-secondary)')}
              onMouseLeave={e => !asset.isBlocked && (e.currentTarget.style.background = 'transparent')}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <div style={{ width: 28, height: 28, borderRadius: 'var(--radius-md)', background: asset.isBlocked ? '#FCEBEB' : '#E6F1FB', border: asset.isBlocked ? '1px solid #F09595' : 'none', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  {asset.isBlocked ? <IconLock size={13} color="var(--color-danger)" stroke={1.5} /> : <IconTruck size={13} color="#185FA5" stroke={1.5} />}
                </div>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 400, color: asset.isBlocked ? 'var(--color-danger)' : 'var(--color-text-primary)' }}>{asset.make} {asset.model}</div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginTop: 2 }}>
                    {asset.reg && <span style={{ fontFamily: 'monospace', fontSize: 10, background: '#FAC775', color: '#412402', padding: '1px 5px', borderRadius: 3 }}>{asset.reg}</span>}
                    {asset.vin && <span style={{ fontFamily: 'monospace', fontSize: 10, color: 'var(--color-text-tertiary)' }}>VIN: {asset.vin.slice(-6)}</span>}
                    {asset.serialNumber && <span style={{ fontFamily: 'monospace', fontSize: 10, color: 'var(--color-text-tertiary)' }}>S/N: {asset.serialNumber}</span>}
                  </div>
                </div>
              </div>
              <div style={{ fontSize: 12, color: asset.isBlocked ? 'var(--color-danger)' : 'var(--color-text-secondary)' }}>{asset.assignedTo || '—'}</div>
              <div style={{ fontSize: 12, color: 'var(--color-text-secondary)' }}>—</div>
              <div style={{ fontSize: 12, color: 'var(--color-text-secondary)' }}>{asset.mot || '—'}</div>
              <div style={{ fontSize: 10, padding: '2px 8px', borderRadius: 20, background: asset.isBlocked ? 'var(--color-danger-light)' : 'var(--color-success-light)', color: asset.isBlocked ? 'var(--color-danger)' : 'var(--color-success)', display: 'inline-flex', alignItems: 'center', gap: 3, width: 'fit-content' }}>
                {asset.isBlocked ? <><IconX size={10} stroke={2} /> Blocked</> : <><IconCheck size={10} stroke={2} /> Available</>}
              </div>
              <div style={{ fontSize: 12, color: 'var(--color-brand)', textAlign: 'right' }}>View</div>
            </div>
          ))}
        </div>
      </div>

      {showModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100 }}>
          <div style={{ background: '#fff', borderRadius: 'var(--radius-xl)', padding: 28, width: 460, border: '0.5px solid var(--color-border-light)', maxHeight: '90vh', overflow: 'auto' }}>

            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
              <div style={{ fontSize: 18, fontWeight: 400 }}>
                {modalStep === 1 ? 'Add asset' : 'Assign to staff member'}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <div style={{ fontSize: 11, color: 'var(--color-text-tertiary)' }}>Step {modalStep} of {assetPool ? 1 : 2}</div>
                <div onClick={resetModal} style={{ cursor: 'pointer', color: 'var(--color-text-tertiary)', fontSize: 20, marginLeft: 8 }}>×</div>
              </div>
            </div>

            {modalStep === 1 && (
              <>
                <div style={{ marginBottom: 14 }}>
                  <label style={{ display: 'block', fontSize: 11, color: 'var(--color-text-secondary)', marginBottom: 6 }}>Asset type</label>
                  <div style={{ display: 'flex', gap: 8 }}>
                    {['vehicle', 'trailer', 'machinery'].map(t => (
                      <div key={t} onClick={() => setAssetType(t)} style={{ flex: 1, padding: '8px', borderRadius: 'var(--radius-md)', border: '0.5px solid', borderColor: assetType === t ? 'var(--color-brand)' : 'var(--color-border-medium)', background: assetType === t ? 'var(--color-brand-light)' : 'transparent', color: assetType === t ? 'var(--color-brand)' : 'var(--color-text-secondary)', fontSize: 12, cursor: 'pointer', textAlign: 'center', transition: 'all 0.15s', textTransform: 'capitalize' }}>
                        {t}
                      </div>
                    ))}
                  </div>
                </div>

                {assetType === 'vehicle' && (
                  <div style={{ marginBottom: 14 }}>
                    <label style={{ display: 'block', fontSize: 11, color: 'var(--color-text-secondary)', marginBottom: 6 }}>Registration number</label>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <input type="text" placeholder="YK23 HBT" value={assetReg} onChange={e => setAssetReg(e.target.value.toUpperCase())} style={{ flex: 1, boxSizing: 'border-box', fontFamily: 'monospace' }} />
                      <button onClick={handleDvlaLookup} disabled={dvlaLoading} style={{ background: 'var(--color-brand)', color: '#fff', border: 'none', borderRadius: 'var(--radius-md)', padding: '0 14px', fontSize: 12, cursor: 'pointer', whiteSpace: 'nowrap' }}>
                        {dvlaLoading ? 'Looking up...' : 'DVLA lookup'}
                      </button>
                    </div>
                  </div>
                )}

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 14 }}>
                  <div>
                    <label style={{ display: 'block', fontSize: 11, color: 'var(--color-text-secondary)', marginBottom: 6 }}>Make</label>
                    <input type="text" placeholder="Ford" value={assetMake} onChange={e => setAssetMake(e.target.value)} style={{ width: '100%', boxSizing: 'border-box' }} />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: 11, color: 'var(--color-text-secondary)', marginBottom: 6 }}>Model</label>
                    <input type="text" placeholder="Transit" value={assetModel} onChange={e => setAssetModel(e.target.value)} style={{ width: '100%', boxSizing: 'border-box' }} />
                  </div>
                </div>

                {assetType === 'vehicle' && (
                  <div style={{ marginBottom: 14 }}>
                    <label style={{ display: 'block', fontSize: 11, color: 'var(--color-text-secondary)', marginBottom: 6 }}>VIN number <span style={{ color: 'var(--color-text-tertiary)' }}>— optional</span></label>
                    <input type="text" placeholder="e.g. WF0XXGCBXKKU12345" value={assetVin} onChange={e => setAssetVin(e.target.value.toUpperCase())} style={{ width: '100%', boxSizing: 'border-box', fontFamily: 'monospace', fontSize: 12 }} />
                    <div style={{ fontSize: 10, color: 'var(--color-text-tertiary)', marginTop: 4 }}>17-character vehicle identification number found on the dashboard or door frame</div>
                  </div>
                )}

                {(assetType === 'trailer' || assetType === 'machinery') && (
                  <div style={{ marginBottom: 14 }}>
                    <label style={{ display: 'block', fontSize: 11, color: 'var(--color-text-secondary)', marginBottom: 6 }}>Serial number <span style={{ color: 'var(--color-text-tertiary)' }}>— optional</span></label>
                    <input type="text" placeholder="e.g. IW126-2023-48291" value={assetSerial} onChange={e => setAssetSerial(e.target.value.toUpperCase())} style={{ width: '100%', boxSizing: 'border-box', fontFamily: 'monospace', fontSize: 12 }} />
                    <div style={{ fontSize: 10, color: 'var(--color-text-tertiary)', marginTop: 4 }}>Found on the manufacturer's plate attached to the chassis</div>
                  </div>
                )}

                <div onClick={() => setAssetPool(!assetPool)} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 14px', background: assetPool ? 'var(--color-brand-light)' : 'var(--color-bg-secondary)', border: `0.5px solid ${assetPool ? 'var(--color-brand)' : 'var(--color-border-light)'}`, borderRadius: 'var(--radius-md)', cursor: 'pointer', marginBottom: 20, transition: 'all 0.15s' }}>
                  <div style={{ width: 20, height: 20, borderRadius: 4, border: `1.5px solid ${assetPool ? 'var(--color-brand)' : 'var(--color-border-medium)'}`, background: assetPool ? 'var(--color-brand)' : '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, transition: 'all 0.15s' }}>
                    {assetPool && <IconCheck size={12} color="#fff" stroke={2.5} />}
                  </div>
                  <div>
                    <div style={{ fontSize: 13, color: assetPool ? 'var(--color-brand)' : 'var(--color-text-primary)' }}>Pool asset</div>
                    <div style={{ fontSize: 11, color: 'var(--color-text-tertiary)', marginTop: 2 }}>Shared between multiple staff — no single person assigned</div>
                  </div>
                </div>

                <div style={{ display: 'flex', gap: 10 }}>
                  <button onClick={resetModal} style={{ flex: 1, background: 'var(--color-bg-secondary)', color: 'var(--color-text-secondary)', border: 'none', borderRadius: 'var(--radius-md)', padding: '10px', fontSize: 13, cursor: 'pointer' }}>Cancel</button>
                  <button onClick={handleNext} disabled={saving || !assetMake || !assetModel} style={{ flex: 2, background: 'var(--color-brand)', color: '#fff', border: 'none', borderRadius: 'var(--radius-md)', padding: '10px', fontSize: 13, cursor: saving || !assetMake || !assetModel ? 'not-allowed' : 'pointer', opacity: saving || !assetMake || !assetModel ? 0.5 : 1 }}>
                    {assetPool ? (saving ? 'Saving...' : 'Add asset') : 'Next — assign staff'}
                  </button>
                </div>
              </>
            )}

            {modalStep === 2 && (
              <>
                <p style={{ fontSize: 13, color: 'var(--color-text-secondary)', marginBottom: 20, lineHeight: 1.6 }}>
                  Select a staff member to assign this {assetType} to. Only operators and drivers from your branch are shown.
                </p>

                {staff.length === 0 ? (
                  <div style={{ padding: '32px 20px', textAlign: 'center', background: 'var(--color-bg-secondary)', borderRadius: 'var(--radius-lg)', marginBottom: 20 }}>
                    <IconUser size={28} color="var(--color-text-tertiary)" stroke={1.5} style={{ marginBottom: 10 }} />
                    <div style={{ fontSize: 14, fontWeight: 400, color: 'var(--color-text-primary)', marginBottom: 6 }}>No staff yet</div>
                    <div style={{ fontSize: 13, color: 'var(--color-text-tertiary)', marginBottom: 16 }}>Invite operators and drivers first, or add as a pool asset</div>
                    <button onClick={() => handleAddAsset(null)} disabled={saving} style={{ background: 'var(--color-bg-primary)', color: 'var(--color-text-secondary)', border: '0.5px solid var(--color-border-medium)', borderRadius: 'var(--radius-md)', padding: '8px 16px', fontSize: 12, cursor: 'pointer' }}>
                      Add as unassigned
                    </button>
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 20 }}>
                    {staff.map((member, i) => {
                      const colours = avatarColours[i % avatarColours.length];
                      const isSelected = assignedTo?.id === member.id;
                      return (
                        <div key={member.id} onClick={() => setAssignedTo(isSelected ? null : member)} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 14px', borderRadius: 'var(--radius-lg)', border: `0.5px solid ${isSelected ? 'var(--color-brand)' : 'var(--color-border-light)'}`, background: isSelected ? 'var(--color-brand-light)' : 'var(--color-bg-primary)', cursor: 'pointer', transition: 'all 0.15s' }}>
                          <div style={{ width: 36, height: 36, borderRadius: '50%', background: colours.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, color: colours.color, flexShrink: 0 }}>
                            {getInitials(member.name)}
                          </div>
                          <div style={{ flex: 1 }}>
                            <div style={{ fontSize: 13, fontWeight: 400, color: 'var(--color-text-primary)' }}>{member.name || member.email}</div>
                            <div style={{ fontSize: 11, color: 'var(--color-text-tertiary)', marginTop: 2, textTransform: 'capitalize' }}>{member.role} · {member.departmentId || 'Workshop'}</div>
                          </div>
                          {isSelected && (
                            <div style={{ width: 22, height: 22, borderRadius: '50%', background: 'var(--color-brand)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                              <IconCheck size={13} color="#fff" stroke={2.5} />
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}

                <div style={{ display: 'flex', gap: 10 }}>
                  <button onClick={() => setModalStep(1)} style={{ flex: 1, background: 'var(--color-bg-secondary)', color: 'var(--color-text-secondary)', border: 'none', borderRadius: 'var(--radius-md)', padding: '10px', fontSize: 13, cursor: 'pointer' }}>Back</button>
                  <button onClick={() => handleAddAsset(assignedTo)} disabled={saving} style={{ flex: 2, background: 'var(--color-brand)', color: '#fff', border: 'none', borderRadius: 'var(--radius-md)', padding: '10px', fontSize: 13, cursor: saving ? 'not-allowed' : 'pointer', opacity: saving ? 0.5 : 1 }}>
                    {saving ? 'Saving...' : assignedTo ? `Assign to ${assignedTo.name?.split(' ')[0]}` : 'Add unassigned'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </Layout>
  );
}